import { useState, type FormEvent, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { CheckCircle2 } from 'lucide-react'
import { api, messaggioErrore } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import styles from './Auth.module.css'

/**
 * Pagina di registrazione nuovo SDR.
 * Quando arrivi qui da un invito team (link con ?email=...&telefono=...)
 * i campi vengono pre-compilati e l'utente deve solo aggiungere nome,
 * cognome e password.
 */
export function Registrati() {
  const [params] = useSearchParams()

  const [nome, setNome] = useState('')
  const [cognome, setCognome] = useState('')
  const [email, setEmail] = useState('')
  const [telefono, setTelefono] = useState('')
  const [password, setPassword] = useState('')
  const [stato, setStato] = useState<'idle' | 'inviato'>('idle')
  const [errore, setErrore] = useState<string | null>(null)
  const [caricamento, setCaricamento] = useState(false)

  // Pre-compila da invito team
  useEffect(() => {
    const e = params.get('email')
    const t = params.get('telefono')
    if (e) setEmail(e)
    if (t) setTelefono(t)
  }, [params])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setErrore(null)
    setCaricamento(true)
    try {
      const res = await api.post<{ dati: { messaggio: string; email: string } }>(
        '/auth/registra',
        { nome, cognome, email, password, telefono },
      )
      setStato('inviato')
      // Salva email per mostrarla nel messaggio di conferma
      setEmail(res.data.dati?.email ?? email)
    } catch (err) {
      setErrore(messaggioErrore(err))
    } finally {
      setCaricamento(false)
    }
  }

  if (stato === 'inviato') {
    return (
      <div className={styles.wrap}>
        <div className={styles.card}>
          <div className={styles.brand}>
            <div className={styles.logoBox}>RC</div>
            <span className={styles.brandTesto}>Ring<strong>Coach</strong></span>
          </div>

          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14,
            color: 'var(--success)',
          }}>
            <CheckCircle2 size={22} />
            <h1 className={styles.titolo} style={{ margin: 0 }}>Registrazione completata</h1>
          </div>

          <p className={styles.descrizione}>
            Ti abbiamo inviato un'email a <strong>{email}</strong> con il link
            per verificare l'account. Una volta confermato potrai accedere e iniziare
            con 3 sessioni gratuite.
          </p>

          <div className={styles.success}>
            Non vedi l'email? Controlla la cartella <strong>spam</strong>.
            Il link scade tra 24 ore.
          </div>

          <div className={styles.linkRiga} style={{ marginTop: 24 }}>
            <Link to="/accedi" className={styles.link}>Vai al login →</Link>
          </div>
        </div>
      </div>
    )
  }

  const passwordCorta = password.length > 0 && password.length < 8

  return (
    <div className={styles.wrap}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <div className={styles.logoBox}>RC</div>
          <span className={styles.brandTesto}>Ring<strong>Coach</strong></span>
        </div>

        <h1 className={styles.titolo}>Crea il tuo account</h1>
        <p className={styles.descrizione}>
          Bastano 30 secondi. Il piano Free include 3 sessioni gratuite di allenamento.
        </p>

        <form onSubmit={submit} className={styles.form}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label className={styles.campo}>
              <span className={styles.lbl}>Nome</span>
              <input
                type="text"
                required
                autoComplete="given-name"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                className={styles.input}
              />
            </label>
            <label className={styles.campo}>
              <span className={styles.lbl}>Cognome</span>
              <input
                type="text"
                required
                autoComplete="family-name"
                value={cognome}
                onChange={(e) => setCognome(e.target.value)}
                className={styles.input}
              />
            </label>
          </div>

          <label className={styles.campo}>
            <span className={styles.lbl}>Email</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={styles.input}
              placeholder="mario@esempio.it"
            />
          </label>

          <label className={styles.campo}>
            <span className={styles.lbl}>
              Numero di cellulare
              <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6 }}>
                (per riconoscerti quando chiami)
              </span>
            </span>
            <input
              type="tel"
              required
              autoComplete="tel"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              className={styles.input}
              placeholder="3471234567"
            />
          </label>

          <label className={styles.campo}>
            <span className={styles.lbl}>Password</span>
            <input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.input}
              placeholder="Almeno 8 caratteri"
              aria-invalid={passwordCorta}
            />
            {passwordCorta && (
              <span style={{ fontSize: 12, color: 'var(--warning)', marginTop: 4 }}>
                Servono almeno 8 caratteri
              </span>
            )}
          </label>

          {errore && <div className={styles.errore}>{errore}</div>}

          <Button type="submit" taglia="lg" caricamento={caricamento}>
            Crea account
          </Button>

          <p style={{
            fontSize: 12,
            color: 'var(--text-muted)',
            textAlign: 'center',
            lineHeight: 1.5,
            margin: 0,
          }}>
            Creando un account accetti i nostri termini di servizio e la privacy policy.
          </p>
        </form>

        <div className={styles.linkRiga}>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Hai già un account?{' '}
            <Link to="/accedi" className={styles.link}>Accedi</Link>
          </span>
        </div>
      </div>
    </div>
  )
}
